import { useCallback } from 'react'
import { LocalFile, RemoteFile } from '@types'
import { getFileUrl } from '@utils/getFileUrl'

const fileUrlCache = new Map<string, { url: string; timestamp: number }>()

const CACHE_TTL = 5 * 60 * 1000

interface UseFilePreviewsProps {
  files?: RemoteFile[]
  onFilesLoaded: (files: (LocalFile | RemoteFile)[]) => void
}

export const useFilePreviews = ({ files, onFilesLoaded }: UseFilePreviewsProps) => {
  const loadFilePreviews = useCallback(async () => {
    if (!files || files.length === 0) {
      onFilesLoaded([])
      return
    }

    try {
      const now = Date.now()

      const promises = files.map(async (file) => {
        if (!file.url) return null

        const cached = fileUrlCache.get(file.url)
        const isValid = cached && now - cached.timestamp < CACHE_TTL

        if (isValid) {
          return {
            ...file,
            url: cached.url,
            deleted: false,
          }
        }

        const resolvedUrl = await getFileUrl(file.url)
        if (resolvedUrl) {
          fileUrlCache.set(file.url, {
            url: resolvedUrl,
            timestamp: now,
          })
        }

        return {
          ...file,
          url: resolvedUrl,
          deleted: false,
        }
      })

      const resolvedFiles = (await Promise.all(promises)).filter(Boolean) as RemoteFile[]
      onFilesLoaded(resolvedFiles)
    } catch (error) {
      console.error('Error loading file previews:', error)
      onFilesLoaded([])
    }
  }, [files, onFilesLoaded])

  return {
    loadFilePreviews,
  }
}
